"use client";

import { useCallback, useState } from "react";
import { useAuth } from "@wtc/ui";
import type { MarketingBrand } from "@/lib/marketing/brands";
import type { GoogleDriveFile } from "@/lib/hooks/google-drive/use-google-drive-browser";

/**
 * A marketing asset that has been materialized out of a brand's Google Drive
 * folder into the public `social-media-assets` bucket. `publicUrl` is the
 * stable, Zapier-fetchable / email-embeddable URL.
 */
export interface MaterializedMarketingAsset {
  readonly publicUrl: string;
  readonly path: string;
  readonly contentType: string;
  readonly driveFileId: string;
  readonly driveFileName: string;
  readonly mediaKind: "image" | "video";
}

interface MaterializeResponse {
  publicUrl?: string;
  path?: string;
  contentType?: string;
  driveFileId?: string;
  driveFileName?: string;
  mediaKind?: string;
  error?: string;
}

/**
 * Wraps the Drive-file -> social-bucket materialization for reuse by any surface
 * that lets a marketer pick a brand asset (social composer, email templates).
 *
 * Usage: render <MarketingAssetLibraryModal> and pass `materializeFile` as its
 * onFileSelect materializer, or call `materializeFile(brand, file)` directly.
 * Only image/video files are accepted (the server enforces this too).
 */
export function useMarketingAssetPicker() {
  const { getAuthToken } = useAuth();
  const [isMaterializing, setIsMaterializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const materializeFile = useCallback(
    async (
      brand: MarketingBrand,
      file: Pick<GoogleDriveFile, "id" | "name" | "isFolder">,
    ): Promise<MaterializedMarketingAsset> => {
      if (file.isFolder) {
        throw new Error("Folders cannot be attached — pick an image or video.");
      }

      setIsMaterializing(true);
      setError(null);
      try {
        const token = getAuthToken();
        const response = await fetch("/api/marketing/assets/materialize", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ brand, driveFileId: file.id }),
        });

        const data = (await response.json()) as MaterializeResponse;
        if (!response.ok || !data.publicUrl) {
          throw new Error(data.error || "Could not attach that file.");
        }

        return {
          publicUrl: data.publicUrl,
          path: data.path ?? "",
          contentType: data.contentType ?? "application/octet-stream",
          driveFileId: data.driveFileId ?? file.id,
          driveFileName: data.driveFileName ?? file.name,
          mediaKind: data.mediaKind === "video" ? "video" : "image",
        };
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "Could not attach that file.";
        setError(message);
        throw e;
      } finally {
        setIsMaterializing(false);
      }
    },
    [getAuthToken],
  );

  return { materializeFile, isMaterializing, error };
}
